import { memo, useCallback, useMemo, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { ActionList } from '@m/alchemy-ui/ActionList';
import { type ActionListItemProps } from '@m/alchemy-ui/ActionListItem';
import { Dialog } from '@m/alchemy-ui/Dialog';
import { type FooterButton } from '@m/alchemy-ui/DialogFooter';
import { MoreMenuButton } from '@m/alchemy-ui/MoreMenuButton';
import { PopperBox } from '@m/alchemy-ui/PopperBox';
import { Select } from '@m/alchemy-ui/Select';
import { DeleteIcon } from '@m/alchemy-ui/icons/DeleteIcon';
import { DragDropIcon } from '@m/alchemy-ui/icons/DragDropIcon';
import { AUTO_PANEL_SIZE, type PanelSize, WIDE_PANEL_SIZE } from '../common/panel-types';
import { PanelSizeMenuLabel, PanelSizeMenuSection } from '../CustomPage.styled';

const messages = defineMessages({
  panelSize: {
    id: 'editCustomPagePanelSize',
    defaultMessage: 'Panel size'
  },
  panelSizeAuto: {
    id: 'editCustomPagePanelSizeAuto',
    defaultMessage: 'Auto'
  },
  panelSizeWide: {
    id: 'editCustomPagePanelSizeWide',
    defaultMessage: 'Wide'
  },
  panelOptions: {
    id: 'editCustomPagePanelOptionsWithPosition',
    defaultMessage: 'Options for {panel}, row {row}, position {position} of {total}'
  },
  movePanel: {
    id: 'editCustomPageMovePanelAction',
    defaultMessage: 'Move panel'
  },
  deletePanel: {
    id: 'editCustomPageDeletePanel',
    defaultMessage: 'Delete panel'
  },
  deletePanelConfirmation: {
    id: 'editCustomPageDeletePanelConfirmation',
    defaultMessage: 'Are you sure you want to delete this element?'
  }
});

interface PanelOptionsMenuProps {
  readonly panelId: string;
  readonly panelName: string;
  readonly rowIndex: number;
  readonly panelIndex: number;
  readonly panelCount: number;
  readonly showSizeSelect: boolean;
  readonly size: PanelSize;
  readonly labelId: string;
  /** Omit both callbacks for an inert copy (drop preview): the delete dialog, which portals, never opens. */
  readonly onSizeChange?: (size: PanelSize) => void;
  readonly onDelete?: () => void;
  readonly onMove?: (trigger: HTMLElement) => void;
}

const PanelOptionsMenuComponent = ({
  panelId,
  panelName,
  rowIndex,
  panelIndex,
  panelCount,
  showSizeSelect,
  size,
  labelId,
  onSizeChange,
  onDelete,
  onMove
}: PanelOptionsMenuProps) => {
  const intl = useIntl();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const sizeOptions = useMemo(
    () => [
      { value: AUTO_PANEL_SIZE as string, label: intl.formatMessage(messages.panelSizeAuto) },
      { value: WIDE_PANEL_SIZE as string, label: intl.formatMessage(messages.panelSizeWide) }
    ],
    [intl]
  );

  const closeConfirm = useCallback(() => setConfirmOpen(false), []);

  const handleConfirmDelete = useCallback(() => {
    setConfirmOpen(false);
    onDelete?.();
  }, [onDelete]);

  // Typed with the test id up front: TS rejects extra keys in object literals passed to Alchemy's config props.
  const footerButtons = useMemo<(FooterButton & { 'data-testid': string })[]>(
    () => [
      {
        label: intl.formatMessage({ id: 'cancel', defaultMessage: 'Cancel' }),
        onClick: closeConfirm,
        priority: 'tertiary',
        'data-testid': 'panel-delete-cancel-button'
      },
      {
        label: intl.formatMessage({ id: 'delete', defaultMessage: 'Delete' }),
        onClick: handleConfirmDelete,
        destructive: true,
        'data-testid': 'panel-delete-confirm-button'
      }
    ],
    [intl, closeConfirm, handleConfirmDelete]
  );

  return (
    <>
      <MoreMenuButton
        ref={triggerRef}
        aria-label={intl.formatMessage(messages.panelOptions, {
          panel: panelName,
          row: rowIndex + 1,
          position: panelIndex + 1,
          total: panelCount
        })}
        placement="bottom-end"
        data-testid="panel-options-button"
        data-panel-options-button={panelId}
        overlay={({ closeAndReturnFocus }) => {
          const actions: (ActionListItemProps & { 'data-testid': string })[] = [
            ...(onMove
              ? [
                  {
                    label: intl.formatMessage(messages.movePanel),
                    icon: <DragDropIcon aria-hidden="true" />,
                    onClick: () => {
                      closeAndReturnFocus();
                      if (triggerRef.current) {
                        onMove(triggerRef.current);
                      }
                    },
                    'data-testid': 'panel-move-action'
                  }
                ]
              : []),
            {
              label: intl.formatMessage(messages.deletePanel),
              icon: <DeleteIcon aria-hidden="true" />,
              onClick: () => {
                closeAndReturnFocus();
                if (onDelete) {
                  setConfirmOpen(true);
                }
              },
              'data-testid': 'panel-delete-action'
            }
          ];

          return (
            <PopperBox hasPadding={false} maxWidth={260}>
              <div data-testid="panel-options-menu">
                {showSizeSelect && (
                  <PanelSizeMenuSection>
                    <PanelSizeMenuLabel id={labelId}>{intl.formatMessage(messages.panelSize)}</PanelSizeMenuLabel>
                    <Select
                      labelId={labelId}
                      isFullWidth
                      value={size}
                      options={sizeOptions}
                      onChange={(option) => {
                        onSizeChange?.(option.value as PanelSize);
                        closeAndReturnFocus();
                      }}
                      data-testid="panel-size-select"
                    />
                  </PanelSizeMenuSection>
                )}
                <ActionList actions={actions} />
              </div>
            </PopperBox>
          );
        }}
      />
      <Dialog
        isOpen={confirmOpen}
        onClose={closeConfirm}
        headerContent={intl.formatMessage({ id: 'areYouSure', defaultMessage: 'Are you sure?' })}
        bodyContent={intl.formatMessage(messages.deletePanelConfirmation)}
        footerButtons={footerButtons}
        data-testid="panel-delete-dialog"
      />
    </>
  );
};

export const PanelOptionsMenu = memo(PanelOptionsMenuComponent);
